import React from 'react';
import { Image, Text, View } from '@react-pdf/renderer';
import { PVM_HEADLINE_COLOR, PVM_TEXT_COLOR } from './pvmBranding';
import { dedupeAllocationByLabel, dedupeTop10ByName } from './dedupeGpsAllocations';
import {
  GPS_DEFAULT_ASSET_ALLOCATION,
  GPS_DEFAULT_SECTOR_ALLOCATION,
  GPS_DEFAULT_TOP10_STOCKS,
} from './gpsAllocationDefaults';

const stockTextStyle = { fontSize: 6.75, color: PVM_TEXT_COLOR, lineHeight: 1 as const };

const formatStockPercentage = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') {
    const normalized = value.trim();
    return normalized.endsWith('%') ? normalized.slice(0, -1) : normalized;
  }
  const numValue = Number(value);
  if (isNaN(numValue)) return '';
  return numValue.toFixed(2).replace('.', ',');
};

export interface GpsPage2RightColumnPdfProps {
  effectiveData: any;
  sectionHeaders: any;
  pdfStyles: any;
  formatPercent: (value: number | string) => string;
  getItemColor: (item: any, index: number, palette?: string[]) => string;
  colorSets?: string[];
  pieChart1ImageDataUrl?: string;
  pieChart2ImageDataUrl?: string;
  templateColor?: string;
}

const renderPieBlock = (
  title: string,
  data: Array<{ label?: string; value?: number | string }>,
  pieImageUrl: string | undefined,
  pdfStyles: any,
  formatPercent: (value: number | string) => string,
  getItemColor: (item: any, index: number, palette?: string[]) => string,
  colorSets?: string[],
) => {
  if (!Array.isArray(data) || data.length === 0) return null;

  return (
    <View style={{ marginBottom: 3, marginTop: 0 }}>
      <Text style={pdfStyles.chartTitle}>{title}</Text>
      <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
        <View style={{ flex: 1, paddingRight: 4, minWidth: 0 }}>
          {data.map((item, index) => (
            <View
              key={index}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                backgroundColor: '#D2D2D4',
                marginBottom: 1,
                paddingHorizontal: 3,
                height: 9.5,
              }}
            >
              <Text style={{ flex: 1, ...stockTextStyle }}>{item.label}</Text>
              <Text
                style={{
                  fontSize: 6.75,
                  textAlign: 'right',
                  paddingRight: 4,
                  width: 38,
                  color: PVM_TEXT_COLOR,
                  lineHeight: 1,
                }}
              >
                {formatPercent(item.value ?? '')}
              </Text>
              <View
                style={{
                  width: 5,
                  height: 5,
                  backgroundColor: getItemColor(item, index, colorSets),
                }}
              />
            </View>
          ))}
        </View>
        <View style={{ width: 64 }}>
          {pieImageUrl ? (
            <Image src={pieImageUrl} style={{ width: 64, height: 64 }} />
          ) : (
            <View style={{ width: 64, height: 64, backgroundColor: '#f0f0f0' }}>
              <Text style={{ fontSize: 6, textAlign: 'center', paddingTop: 26 }}>Loading...</Text>
            </View>
          )}
        </View>
      </View>
    </View>
  );
};

export const GpsPage2RightColumnPdf: React.FC<GpsPage2RightColumnPdfProps> = ({
  effectiveData,
  sectionHeaders,
  pdfStyles,
  formatPercent,
  getItemColor,
  colorSets,
  pieChart1ImageDataUrl,
  pieChart2ImageDataUrl,
  templateColor = PVM_HEADLINE_COLOR,
}) => {
  const additional = effectiveData?.additional_fields || {};
  const assetAllocation = dedupeAllocationByLabel(
    (effectiveData.assetAllocation?.length
      ? effectiveData.assetAllocation
      : additional.assetAllocation?.length
        ? additional.assetAllocation
        : GPS_DEFAULT_ASSET_ALLOCATION) || [],
  );
  const sectorAllocation = dedupeAllocationByLabel(
    (effectiveData.sectorAllocation?.length
      ? effectiveData.sectorAllocation
      : additional.sectorAllocation?.length
        ? additional.sectorAllocation
        : GPS_DEFAULT_SECTOR_ALLOCATION) || [],
  );
  const top10Stocks = dedupeTop10ByName(
    (effectiveData.top10Stocks?.length
      ? effectiveData.top10Stocks
      : additional.top10Stocks?.length
        ? additional.top10Stocks
        : GPS_DEFAULT_TOP10_STOCKS) || [],
  );
  const top10Title = sectionHeaders?.top10Stocks || 'TOP-10 AKTIEN (in %)';

  return (
    <View style={{ width: '100%' }}>
      {renderPieBlock(
        sectionHeaders.assetAllocation || 'VERMÖGENSAUFTEILUNG',
        assetAllocation,
        pieChart1ImageDataUrl,
        pdfStyles,
        formatPercent,
        getItemColor,
        colorSets,
      )}
      {renderPieBlock(
        sectionHeaders.sectorAllocation || 'BRANCHEN (AKTIENANTEIL)',
        sectorAllocation,
        pieChart2ImageDataUrl,
        pdfStyles,
        formatPercent,
        getItemColor,
        colorSets,
      )}
      {top10Stocks.length > 0 && (
        <View style={{ marginTop: 2, width: '100%' }}>
          <Text
            style={[
              pdfStyles.chartTitle || pdfStyles.sectionTitle,
              {
                color: templateColor,
                borderBottomColor: templateColor,
                marginBottom: 1,
              },
            ]}
          >
            {top10Title}
          </Text>
          <View style={{ backgroundColor: '#D2D2D4' }}>
            {top10Stocks.map((stock, index) => (
              <View
                key={index}
                style={{
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  paddingHorizontal: 3,
                  paddingVertical: 1,
                  // Same white split as pie-chart legend rows
                  borderBottomWidth: index < top10Stocks.length - 1 ? 2 : 0,
                  borderBottomColor: '#ffffff',
                }}
              >
                <Text style={{ width: 14, ...stockTextStyle }}>{index + 1}.</Text>
                <Text style={{ flex: 1, ...stockTextStyle, paddingRight: 4 }}>{stock.name}</Text>
                <Text style={{ ...stockTextStyle, textAlign: 'right', width: 36 }}>
                  {formatStockPercentage(stock.percentage)}%
                </Text>
              </View>
            ))}
          </View>
        </View>
      )}
    </View>
  );
};
